import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import type { Offer } from '@/api/types';
import { getConversionByCountry } from '@/api/stats';
import {
  COLUMNS,
  ALL_COLUMN_KEYS,
  buildExportRows,
  toCsv,
  toXlsx,
  downloadCsv,
  downloadXlsx,
  type ColumnKey,
} from '@/lib/offersExport';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  offers: Offer[];
}

type Format = 'csv' | 'xlsx';
type RangePreset = 7 | 30 | 90 | 'custom';

const STORAGE_KEY = 'offersExport.columns';

function loadColumns(): Set<ColumnKey> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const keys = JSON.parse(raw) as ColumnKey[];
      const valid = keys.filter((k) => ALL_COLUMN_KEYS.includes(k));
      if (valid.length > 0) return new Set(valid);
    }
  } catch {
    /* ignore malformed storage */
  }
  return new Set(ALL_COLUMN_KEYS);
}

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

export function ExportModal({ isOpen, onClose, offers }: ExportModalProps) {
  const [format, setFormat] = useState<Format>('csv');
  const [rangePreset, setRangePreset] = useState<RangePreset>(30);
  const [customFrom, setCustomFrom] = useState(() => isoDaysAgo(30));
  const [customTo, setCustomTo] = useState(() => isoDaysAgo(0));
  const [selected, setSelected] = useState<Set<ColumnKey>>(() => loadColumns());
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear any stale export error when the dialog is (re)opened.
  useEffect(() => {
    if (isOpen) setError(null);
  }, [isOpen]);

  // Persist column selection whenever it changes.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...selected]));
    } catch {
      /* ignore quota errors */
    }
  }, [selected]);

  const groups = useMemo(
    () => ({
      offer: COLUMNS.filter((c) => c.group === 'offer'),
      performance: COLUMNS.filter((c) => c.group === 'performance'),
    }),
    [],
  );

  const toggleColumn = (key: ColumnKey) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const setGroup = (group: 'offer' | 'performance', on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const c of groups[group]) {
        if (on) next.add(c.key);
        else next.delete(c.key);
      }
      return next;
    });

  const customInvalid = rangePreset === 'custom' && customFrom > customTo;
  const canExport = selected.size > 0 && offers.length > 0 && !exporting && !customInvalid;

  const handleExport = async () => {
    if (!canExport) return;
    setExporting(true);
    setError(null);
    try {
      const range =
        rangePreset === 'custom' ? { start: customFrom, end: customTo } : rangePreset;
      const conv = await getConversionByCountry(range);
      const map = new Map(conv.map((cc) => [cc.country.toUpperCase(), cc]));
      const rows = buildExportRows(offers, map);
      const keys = ALL_COLUMN_KEYS.filter((k) => selected.has(k));
      const today = new Date().toISOString().slice(0, 10);
      if (format === 'csv') {
        downloadCsv(`offers-export-${today}.csv`, toCsv(rows, keys));
      } else {
        downloadXlsx(`offers-export-${today}.xlsx`, await toXlsx(rows, keys));
      }
      onClose();
    } catch {
      setError('Failed to load conversion data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const renderGroup = (title: string, group: 'offer' | 'performance') => {
    const cols = groups[group];
    const allOn = cols.every((c) => selected.has(c.key));
    const someOn = cols.some((c) => selected.has(c.key));
    return (
      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-content mb-2">
          <input
            type="checkbox"
            ref={(el) => {
              if (el) el.indeterminate = someOn && !allOn;
            }}
            checked={allOn}
            onChange={(e) => setGroup(group, e.target.checked)}
          />
          {title}
        </label>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 pl-6">
          {cols.map((c) => (
            <label key={c.key} className="flex items-center gap-2 text-sm text-content-muted">
              <input
                type="checkbox"
                checked={selected.has(c.key)}
                onChange={() => toggleColumn(c.key)}
              />
              {c.header}
            </label>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export offers" size="xl">
      <div className="space-y-6">
        {/* Format */}
        <div>
          <div className="text-sm font-medium text-content mb-2">Format</div>
          <div className="flex gap-4">
            {(['csv', 'xlsx'] as Format[]).map((f) => (
              <label key={f} className="flex items-center gap-2 text-sm text-content-muted">
                <input
                  type="radio"
                  name="export-format"
                  checked={format === f}
                  onChange={() => setFormat(f)}
                />
                {f.toUpperCase()}
              </label>
            ))}
          </div>
        </div>

        {/* Date range */}
        <div>
          <div className="text-sm font-medium text-content mb-2">Date range</div>
          <select
            value={String(rangePreset)}
            onChange={(e) =>
              setRangePreset(
                e.target.value === 'custom' ? 'custom' : (Number(e.target.value) as RangePreset),
              )
            }
            className="h-9 rounded-md border border-border-strong bg-surface text-content-muted text-sm px-2 focus:outline-none focus:ring-2 focus:ring-border-strong"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="custom">Custom…</option>
          </select>
          {rangePreset === 'custom' && (
            <div className="mt-3 flex items-center gap-2">
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="h-9 rounded-md border border-border-strong bg-surface text-content-muted text-sm px-2"
              />
              <span className="text-content-subtle">—</span>
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="h-9 rounded-md border border-border-strong bg-surface text-content-muted text-sm px-2"
              />
            </div>
          )}
          {customInvalid && (
            <p className="mt-2 text-sm text-danger">Start date must be on or before end date.</p>
          )}
        </div>

        {/* Columns */}
        <div className="space-y-4">
          <div className="text-sm font-medium text-content">Columns</div>
          {renderGroup('Offer fields', 'offer')}
          {renderGroup('Performance', 'performance')}
          {selected.size === 0 && (
            <p className="text-sm text-danger">Select at least one column.</p>
          )}
        </div>

        {error && <p className="text-sm text-danger">{error}</p>}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleExport} loading={exporting} disabled={!canExport}>
            Export
          </Button>
        </div>
      </div>
    </Modal>
  );
}
